/** How a prescribed load is measured. "bodyweight" is a third answer, not a missing unit. */
export type WeightUnit = "lb" | "kg" | "bodyweight";

/**
 * One exercise inside a plan (PRD §6.2: "each with sets, reps, load/notes, and an optional video").
 *
 * Every field except `name` is optional, and plans written before a field existed simply don't carry
 * it -- those plans stay valid and render without the missing detail rather than needing a backfill.
 * Absence is also a legitimate ongoing state: a coach may prescribe an exercise with no specific load
 * ("easy spin, 5 minutes"), so absent is meaningfully different from zero.
 */
export interface PlanElement {
  name: string;
  /**
   * How the work is measured.
   *
   * Not everything is countable. "Hold a plank for 30 seconds" is a real prescription that reps cannot
   * express, and forcing it into a reps field would either read as 30 repetitions or be smuggled into
   * the load/notes text where nothing can act on it.
   *
   * Absent means "reps", because every plan written before this existed was reps-based. Reading it
   * through `measureOf` rather than the raw field keeps that default in one place.
   */
  measure?: "reps" | "time";
  sets?: number;
  /** When measured in reps. */
  reps?: number;
  /** When measured in time -- the length of one set's hold or effort. */
  durationSeconds?: number;
  /**
   * The prescribed load as free text -- kept for plans written before `loadValue`/`loadUnit` existed.
   *
   * A bare string like "35 lb" or "bodyweight" with no structure meant nothing could ever convert it:
   * the athlete's screen used to append whichever unit *they* had selected onto whatever the coach had
   * typed, so a coach on lb typing "10" was read back to a kg-preferring athlete as "Coach said: 10 kg"
   * -- wrong, and in the dangerous direction for a child loading a bar. New plans use the structured
   * fields below; this stays only as a display fallback for plans that predate them, and is rendered
   * exactly as typed, with no unit invented or appended, because we do not know what unit it is in.
   */
  load?: string;
  /** The prescribed load as a number, in `loadUnit`. Absent when `loadUnit` is "bodyweight", or unset. */
  loadValue?: number;
  /**
   * What `loadValue` is measured in, chosen by the coach at the point they type it.
   *
   * Recording the unit is what makes conversion possible: `describeLoad` can convert a coach's "35 lb"
   * into "15.9 kg" for an athlete who logs in kg, which a bare number never could. "bodyweight" is a
   * third answer, not a missing one -- most off-ice work has no external load at all.
   */
  loadUnit?: WeightUnit;
  /**
   * Coaching cues for this exercise, seeded from the library entry's description when the coach picks
   * one and editable per plan afterwards.
   *
   * Deliberately not the same field as `load`. `load`/`loadValue` are the prescription and are rendered
   * inline as part of "3 × 8 · 35 lb"; notes are prose the athlete reads while doing the exercise
   * ("keep the knee tracking over the toe"). Putting cues in `load` would wreck that one-line summary.
   */
  notes?: string;
  /** Pause between sets of THIS exercise. */
  restBetweenSetsSeconds?: number;
  /** Pause after this exercise finishes, before the next one starts. */
  restAfterSeconds?: number;
  libraryEntryId?: string;
  /**
   * The circuit this exercise belongs to, if any.
   *
   * Circuits are expressed as a flat list plus grouping metadata rather than as a nested structure, and
   * that is deliberate: every logged session records an `element_index` into this array, so nesting the
   * elements would either invalidate a year of history or force an index that is a path rather than a
   * number. Elements sharing a groupId are contiguous, and the group says how many times to go round.
   */
  groupId?: string;
}

/**
 * A circuit: a run of exercises done in order and repeated.
 *
 * Rounds are the sets. An exercise inside a circuit does one go per round, so its own `sets` is ignored
 * -- "3 rounds of five exercises" is how coaches describe this, and a per-exercise set count inside a
 * round is a different thing nobody asked for. That also means a round maps exactly onto the `setIndex`
 * a session already logs: round 3 of the second exercise is elementIndex 1, setIndex 2, with no change
 * to how sessions are stored or read back.
 */
export interface PlanGroup {
  id: string;
  /** What the coach calls it -- "Conditioning circuit". Optional; falls back to "Circuit". */
  name?: string;
  rounds: number;
  /** The breather after finishing a full round, before going back to the top. */
  restBetweenRoundsSeconds?: number;
}

/** A plan's contents: the exercises in order, and any circuits over them. */
export interface PlanBody {
  elements: PlanElement[];
  groups: PlanGroup[];
}

/** Absent measure means reps -- every plan written before the field existed was reps-based. */
export function measureOf(element: PlanElement): "reps" | "time" {
  return element.measure === "time" ? "time" : "reps";
}

/**
 * A plan's stored contents, in either shape it can have.
 *
 * A bare array is every plan written before circuits existed, and stays valid forever -- an object with
 * `elements` and `groups` is what the builder writes now. Reading both here means no plan needs
 * migrating and no caller needs to know which era it came from.
 */
export function parsePlanBody(elements: unknown): PlanBody {
  const raw = typeof elements === "string" ? safeParse(elements) : elements;
  if (Array.isArray(raw)) return { elements: raw as PlanElement[], groups: [] };
  if (raw && typeof raw === "object") {
    const body = raw as { elements?: unknown; groups?: unknown };
    return {
      elements: Array.isArray(body.elements) ? (body.elements as PlanElement[]) : [],
      groups: Array.isArray(body.groups) ? (body.groups as PlanGroup[]) : [],
    };
  }
  return { elements: [], groups: [] };
}

export function parseElements(elements: unknown): PlanElement[] {
  return parsePlanBody(elements).elements;
}

/** The circuit an exercise is in, if it is in one. */
export function groupOf(body: PlanBody, element: PlanElement | undefined): PlanGroup | null {
  if (!element?.groupId) return null;
  return body.groups.find((group) => group.id === element.groupId) ?? null;
}

/**
 * How many times an exercise is performed: its own sets, or the rounds of the circuit it is in.
 *
 * One rule in one place, because it is the thing every screen has to agree on -- the walkthrough
 * counting, the prescription line, and the session detail page comparing what was done against what was
 * asked for.
 */
export function repetitionsFor(body: PlanBody, index: number): number {
  const element = body.elements[index];
  const group = groupOf(body, element);
  if (group) return Math.max(1, group.rounds);
  return Math.max(1, element?.sets ?? 1);
}

/** The contiguous run of element indexes belonging to a circuit. */
export function groupRange(body: PlanBody, groupId: string): { first: number; last: number } | null {
  const indexes = body.elements
    .map((element, index) => (element.groupId === groupId ? index : -1))
    .filter((index) => index >= 0);
  if (indexes.length === 0) return null;
  return { first: indexes[0], last: indexes[indexes.length - 1] };
}

function safeParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return [];
  }
}

/** "90s", or "2m 30s" once it's long enough that counting seconds stops being useful. */
export function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder === 0 ? `${minutes}m` : `${minutes}m ${remainder}s`;
}

/**
 * Human-readable prescription line, e.g. "3 × 8 · 35 lb" or "3 × 30s · rest 45s".
 *
 * Returns null when an element carries nothing worth saying, so callers can omit the line entirely
 * rather than rendering an empty or "undefined" one.
 */
/**
 * A circuit's own line: "3 rounds · rest 60s between rounds".
 *
 * Separate from the per-exercise prescription because it describes the loop, not the work -- the
 * exercises inside keep their own reps and holds, and repeating the round count on each of them would
 * read as five separate three-set exercises rather than one circuit.
 */
export function describeCircuit(group: PlanGroup): string {
  const parts = [`${group.rounds} round${group.rounds === 1 ? "" : "s"}`];
  if (group.restBetweenRoundsSeconds) {
    parts.push(`rest ${formatSeconds(group.restBetweenRoundsSeconds)} between rounds`);
  }
  return parts.join(" · ");
}

/**
 * Pounds per kilogram, to enough precision that a round trip (lb -> kg -> lb) never drifts by a
 * visible amount at the one decimal place this is displayed to.
 */
const LB_PER_KG = 2.2046226218;

/** Converts a weight between lb and kg. "bodyweight" has no numeric value, so it is never a unit here. */
export function convertWeight(value: number, from: "lb" | "kg", to: "lb" | "kg"): number {
  if (from === to) return value;
  return from === "lb" ? value / LB_PER_KG : value * LB_PER_KG;
}

/** A weight for display: one decimal place, without a trailing ".0" for a whole number. */
export function formatWeight(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
}

/**
 * The load part of a prescription, in the unit the *reader* wants rather than the unit the coach typed.
 *
 * This is the fix for the reported bug: a coach on lb prescribing "10" used to reach a kg-preferring
 * athlete as "Coach said: 10 kg" -- the athlete's own unit relabelling the coach's number rather than
 * converting it. With the load stored as a number plus its real unit, this converts properly; without
 * `displayUnit` (every coach-facing screen -- the builder, the plan detail, session history) it shows
 * the load in the unit the coach actually entered, unconverted, which is what "what did I prescribe"
 * has to mean.
 */
export function describeLoad(element: PlanElement, displayUnit?: WeightUnit): string | null {
  if (element.loadUnit === "bodyweight") return "bodyweight";
  if (element.loadUnit && element.loadValue !== undefined) {
    const shouldConvert = displayUnit && displayUnit !== "bodyweight" && displayUnit !== element.loadUnit;
    return shouldConvert
      ? `${formatWeight(convertWeight(element.loadValue, element.loadUnit, displayUnit))} ${displayUnit}`
      : `${formatWeight(element.loadValue)} ${element.loadUnit}`;
  }
  // A plan written before loadValue/loadUnit existed. Shown exactly as typed, with no unit invented or
  // appended -- appending a guessed unit is the exact mistake this replaces.
  return element.load?.trim() || null;
}

/**
 * Whether this exercise has a load worth asking the athlete about at all -- either a structured one or
 * a legacy free-text one. Drives whether the walkthrough opens the weight question by default, or
 * leaves it behind the "I used weight for this" button.
 */
export function hasPrescribedLoad(element: PlanElement): boolean {
  return element.loadUnit !== undefined || Boolean(element.load?.trim());
}

/**
 * The prescribed load as a plain number in `unit`, for pre-filling the athlete's own numeric entry --
 * unlike `describeLoad`, this carries no unit suffix, and returns null when there is nothing sensibly
 * numeric to offer.
 *
 * A legacy free-text load only counts when it is a bare number ("8", with nothing else): that is the
 * one shape the old numeric prefill ever handled correctly, and anything else ("35 lb", "resistance
 * band") cannot be safely assumed to be in `unit` -- it is left for `describeLoad` to show as typed.
 */
export function loadValueInUnit(element: PlanElement, unit: "lb" | "kg"): number | null {
  if (element.loadUnit === "bodyweight") return null;
  if (element.loadUnit && element.loadValue !== undefined) {
    return convertWeight(element.loadValue, element.loadUnit, unit);
  }
  if (element.loadUnit) return null;
  const bare = element.load?.trim();
  return bare && /^\d+(?:\.\d+)?$/.test(bare) ? Number(bare) : null;
}

/**
 * The prescription for one exercise.
 *
 * `inCircuit` drops the set count, because inside a circuit the rounds are the sets and they belong to
 * the circuit's line rather than to each exercise in it -- "3 × 10 reps" repeated under a heading that
 * already says "3 rounds" reads as nine sets.
 *
 * `displayUnit`, when given, converts the load into that unit for the reader -- see `describeLoad`.
 * Left out on every coach-facing screen, which shows the load exactly as prescribed.
 */
export function describePrescription(element: PlanElement, inCircuit = false, displayUnit?: WeightUnit): string | null {
  const isTime = measureOf(element) === "time";
  const amount = isTime
    ? element.durationSeconds
      ? formatSeconds(element.durationSeconds)
      : null
    : element.reps
      ? `${element.reps}`
      : null;

  const sets = inCircuit ? undefined : element.sets;
  const setsAmount =
    sets && amount
      ? `${sets} × ${amount}`
      : sets
        ? `${sets} sets`
        : amount
          ? isTime
            ? amount
            : `${amount} reps`
          : null;

  const parts = [
    setsAmount,
    describeLoad(element, displayUnit),
    // Only worth showing when there is more than one set to rest between.
    !inCircuit && element.restBetweenSetsSeconds && element.sets && element.sets > 1
      ? `rest ${formatSeconds(element.restBetweenSetsSeconds)}`
      : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * Roughly how long one rep takes.
 *
 * A deliberate approximation. The app knows every rep, hold and rest but not tempo, and pretending to
 * a precision it does not have would be worse than a stated estimate -- which is why what this feeds
 * is rendered as "about 14 minutes" rather than a countdown.
 */
const SECONDS_PER_REP = 3;

/**
 * How long a session will take, in seconds.
 *
 * Nothing on the athlete's overview answered "how long will this take?" (UX review F56), which is
 * Maya's single most-asked question and the one that decides whether she starts at all -- and Julie's
 * question too, deciding whether there is time before dinner. Every input needed was already here.
 *
 * Counts the work and the rests the walkthrough will actually impose, so the estimate matches the
 * session rather than an idealised version of it: a circuit's exercises run once per round, and the
 * rest after the last exercise is left out because the session ends there rather than pausing.
 */
export function estimateSessionSeconds(body: PlanBody): number {
  let total = 0;

  body.elements.forEach((element, index) => {
    const group = groupOf(body, element);
    // Inside a circuit the rounds are the sets, and they are counted once for the whole loop below
    // rather than per exercise -- otherwise a 3-round circuit of 5 exercises counts as 15 rounds.
    const repetitions = group ? 1 : Math.max(1, element.sets ?? 1);
    const perSet =
      measureOf(element) === "time"
        ? (element.durationSeconds ?? 0)
        : (element.reps ?? 0) * SECONDS_PER_REP;

    total += repetitions * perSet;
    // Between-set rests happen one fewer time than there are sets.
    total += Math.max(0, repetitions - 1) * (element.restBetweenSetsSeconds ?? 0);
    // The pause after this exercise, unless it is the last thing in the session.
    if (index < body.elements.length - 1) total += element.restAfterSeconds ?? 0;
  });

  // Each circuit multiplies the work of the exercises inside it by its rounds, and adds a rest per
  // round bar the last.
  body.groups.forEach((group) => {
    const range = groupRange(body, group.id);
    if (!range) return;
    const rounds = Math.max(1, group.rounds ?? 1);
    let perRound = 0;
    for (let i = range.first; i <= range.last; i++) {
      const element = body.elements[i];
      if (!element) continue;
      perRound +=
        measureOf(element) === "time"
          ? (element.durationSeconds ?? 0)
          : (element.reps ?? 0) * SECONDS_PER_REP;
    }
    // The single pass through was already counted above, so only the extra rounds are added here.
    total += (rounds - 1) * perRound;
    total += Math.max(0, rounds - 1) * (group.restBetweenRoundsSeconds ?? 0);
  });

  return total;
}

/**
 * How many things the athlete has to get through: exercises, with a circuit counted per round.
 *
 * "7 blocks" is a more honest answer to "how much is left" than "3 exercises" when two of those three
 * are a circuit done three times.
 */
export function countSessionBlocks(body: PlanBody): number {
  return body.elements.reduce((count, element) => {
    const group = groupOf(body, element);
    if (!group) return count + Math.max(1, element.sets ?? 1);
    return count + Math.max(1, group.rounds ?? 1);
  }, 0);
}

/**
 * "About 14 minutes · 7 blocks", or just the blocks when there is nothing to time against.
 *
 * Rounded to the nearest minute and prefixed with "about", because this is an estimate built on an
 * assumed tempo -- see SECONDS_PER_REP. A plan of untimed, repless exercises yields no estimate at
 * all rather than a confident "about 0 minutes".
 */
export function describeSessionLength(body: PlanBody): string | null {
  const blocks = countSessionBlocks(body);
  if (blocks === 0) return null;

  const blockLabel = `${blocks} block${blocks === 1 ? "" : "s"}`;
  const seconds = estimateSessionSeconds(body);
  if (seconds <= 0) return blockLabel;

  const minutes = Math.max(1, Math.round(seconds / 60));
  return `About ${minutes} minute${minutes === 1 ? "" : "s"} · ${blockLabel}`;
}
